import React from 'react';
import ReactDOM from 'react-dom/client';
import { createConsentCore, registerGA4, registerMetaPixel } from '@digiwedge/cmp-consent-core';
import { ConsentProvider, ConsentBanner, ConsentDialog } from '@digiwedge/cmp-consent-react';
import { AppThemeProvider } from '@digiwedge/ui-theme';
import '@digiwedge/ui-antd/index.css';
import { App } from './app/app.js';
import { ThemedCmp } from './app/components/ThemedCmp.js';
import { validateEnv } from './env.validate.js';
import './global.css';

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// Validate Vite environment early (non-blocking warnings in dev)
try {
  validateEnv();
} catch {
  // ignore
}

// Initialize CMP (default deny non-essential)
const consent = createConsentCore({
  version: 1,
  store: 'localStorage',
  geofencing: { eeaRequiresOptIn: true },
});

// Register deferred third-party tags
const importMetaEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
const GA_ID = importMetaEnv?.['VITE_GA_ID'];
const FB_PIXEL = importMetaEnv?.['VITE_FB_PIXEL'];
if (GA_ID) {
  consent.tags.register({
    id: 'ga4',
    category: 'analytics',
    loader: () => registerGA4({ measurementId: GA_ID }),
    enabled: false,
  });
}
if (FB_PIXEL) {
  consent.tags.register({
    id: 'fbp',
    category: 'marketing',
    loader: () => registerMetaPixel({ pixelId: FB_PIXEL }),
    enabled: false,
  });
}

// Reflect consent updates to GTM Consent Mode when gtag is present
consent.subscribe((s) => {
  const g: undefined | ((...a: unknown[]) => void) = (
    window as unknown as { gtag?: (...args: unknown[]) => void }
  ).gtag;
  if (!g) return;
  g('consent', 'update', {
    analytics_storage: s.categories.analytics ? 'granted' : 'denied',
    ad_storage: s.categories.marketing ? 'granted' : 'denied',
    ad_user_data: s.categories.marketing ? 'granted' : 'denied',
    ad_personalization: s.categories.marketing ? 'granted' : 'denied',
  });
});

root.render(
  <AppThemeProvider>
    <ConsentProvider core={consent}>
      <App />
      <ThemedCmp>
        <ConsentBanner />
        <ConsentDialog />
      </ThemedCmp>
    </ConsentProvider>
  </AppThemeProvider>,
);
